import type { Icon } from "@phosphor-icons/react/dist/lib/types";
import { BuildingsIcon } from "@phosphor-icons/react/dist/ssr/Buildings";
import { CarProfileIcon } from "@phosphor-icons/react/dist/ssr/CarProfile";
import { CreditCardIcon } from "@phosphor-icons/react/dist/ssr/CreditCard";
import { GlobeIcon } from "@phosphor-icons/react/dist/ssr/Globe";
import { PawPrintIcon } from "@phosphor-icons/react/dist/ssr/PawPrint";
import { RobotIcon } from "@phosphor-icons/react/dist/ssr/Robot";
import { ScissorsIcon } from "@phosphor-icons/react/dist/ssr/Scissors";
import { SpeakerHighIcon } from "@phosphor-icons/react/dist/ssr/SpeakerHigh";
import { StorefrontIcon } from "@phosphor-icons/react/dist/ssr/Storefront";
import { ToothIcon } from "@phosphor-icons/react/dist/ssr/Tooth";

import type { PlatformSetting, PlatformSettingUpdate } from "@/hooks/api/use-platform-settings";

export const AGENT_TEMPLATE_GROUP = "Agent templates";
export const VOICES_GROUP = "Voices";
export const WORKSPACE_GROUP = "Platform workspace";

export const AGENT_TEMPLATE_PREFIX = "AGENT_TEMPLATE_";
export const VOICE_OPTIONS_KEY = "VOICE_OPTIONS";

// Displayed for reference only — the value is managed by the backend at boot.
export const READ_ONLY_KEYS = new Set(["PLATFORM_ACCOUNT_ID"]);

export interface SectionMeta {
	/** Nav / heading title (may differ from the raw API group name). */
	title: string;
	icon: Icon;
	/** One-line description rendered under the section heading. */
	description: string;
}

// Known groups in display order. Groups the API stops returning simply
// disappear; groups we do not know about render generically at the end.
export const KNOWN_GROUP_ORDER = ["Billing", "Website import", AGENT_TEMPLATE_GROUP, VOICES_GROUP, WORKSPACE_GROUP];

export const SECTION_META: Record<string, SectionMeta> = {
	Billing: {
		title: "Billing",
		icon: CreditCardIcon,
		description: "Stripe keys and billing behavior for customer subscriptions.",
	},
	"Website import": {
		title: "Website import",
		icon: GlobeIcon,
		description: "Crawler used to import business websites into the knowledge base.",
	},
	[AGENT_TEMPLATE_GROUP]: {
		title: "Agent templates",
		icon: RobotIcon,
		description: "The starting agent each new customer gets, by business type.",
	},
	[VOICES_GROUP]: {
		title: "Voices",
		icon: SpeakerHighIcon,
		description: "These are the voices new customers can choose for their agent.",
	},
	[WORKSPACE_GROUP]: {
		title: "Workspace",
		icon: BuildingsIcon,
		description: "The platform-owned workspace that holds shared agents, connections, and voices.",
	},
};

// Short helper one-liners rendered under each setting label.
export const SETTING_HELP: Record<string, string> = {
	SMB_SIGNUPS_ENABLED: "Leave empty to keep self-service signup open; 0, false or off closes it.",
	NUMBER_WAITLIST_NOTIFY_EMAILS: "Who to email when a customer joins the number waitlist. Comma-separated.",
	STRIPE_SECRET_KEY: "Secret key from the Stripe dashboard (Developers → API keys).",
	STRIPE_WEBHOOK_SECRET: "Signing secret from your Stripe webhook endpoint settings.",
	STRIPE_PORTAL_RETURN_URL: "Where Stripe sends customers back after they leave the billing portal.",
	STRIPE_METER_EVENT_NAME: "Billing meter event name used to report call minutes to Stripe.",
	FIRECRAWL_API_KEY: "Optional — when set, website imports use Firecrawl instead of the built-in crawler.",
	PLATFORM_ACCOUNT_ID: "Account id of the AX Platform workspace, written automatically at boot.",
};

export interface BusinessTypeMeta {
	title: string;
	icon: Icon;
	description: string;
}

// Presentation for the AGENT_TEMPLATE_<vertical> mapping cards.
export const BUSINESS_TYPES: Record<string, BusinessTypeMeta> = {
	salon: { title: "Salon", icon: ScissorsIcon, description: "New salon signups start from this agent." },
	barbershop: { title: "Barbershop", icon: StorefrontIcon, description: "New barbershop signups start from this agent." },
	auto_shop: { title: "Auto shop", icon: CarProfileIcon, description: "New auto shop signups start from this agent." },
	vet_clinic: { title: "Vet clinic", icon: PawPrintIcon, description: "New vet clinic signups start from this agent." },
	medical_dental: {
		title: "Medical / dental",
		icon: ToothIcon,
		description: "New medical or dental signups start from this agent.",
	},
	generic: {
		title: "Generic",
		icon: StorefrontIcon,
		description: "Fallback for any business type without a mapping of its own.",
	},
};

export function parseVoiceIds(value: string | null | undefined): string[] {
	if (!value) {
		return [];
	}
	try {
		const parsed: unknown = JSON.parse(value);
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
	} catch {
		return [];
	}
}

function sameIds(a: string[], b: string[]): boolean {
	return a.length === b.length && a.every((id, index) => id === b[index]);
}

/**
 * Turns the draft map into the PUT payload. A key is included only when its
 * draft actually differs from the current value; secrets are included only
 * when a new value was typed (an empty masked input means "unchanged").
 */
export function buildUpdates(items: PlatformSetting[], drafts: Record<string, string>): PlatformSettingUpdate[] {
	return items.flatMap((item) => {
		const draft = drafts[item.key];

		if (draft === undefined || READ_ONLY_KEYS.has(item.key)) {
			return [];
		}

		if (item.isSecret) {
			return draft.length > 0 ? [{ key: item.key, value: draft }] : [];
		}

		if (item.key === VOICE_OPTIONS_KEY) {
			const next = parseVoiceIds(draft);
			if (sameIds(next, parseVoiceIds(item.value))) {
				return [];
			}
			// An empty curation clears the row — onboarding falls back to the
			// static voice list.
			return [{ key: item.key, value: next.length > 0 ? JSON.stringify(next) : null }];
		}

		if (draft === (item.value ?? "")) {
			return [];
		}

		// Emptying a field asks the backend to drop the DB override so the env var applies
		// again. With no override there is nothing to drop, and sending it anyway would
		// report a save that changed nothing while the field snaps back to the env value.
		if (draft.length === 0 && item.source !== "db") {
			return [];
		}

		// Template mappings clear to null (backend removes the row) rather than
		// storing an empty string.
		if (item.key.startsWith(AGENT_TEMPLATE_PREFIX)) {
			return [{ key: item.key, value: draft.length > 0 ? draft : null }];
		}

		return [{ key: item.key, value: draft }];
	});
}

export function getSecretStatus(item: PlatformSetting): { label: string; color: "success" | "info" | "default" } {
	if (item.source === "db") {
		return { label: "Set via panel", color: "success" };
	}
	if (item.source === "env") {
		return { label: "Set via env", color: "info" };
	}
	return { label: "Not set", color: "default" };
}
